import {
  IUnderwritingQuoteExcessLayerParticipant,
  getDisplayNameFromEntity,
  useGetEntities,
} from '@converge/graphql';

import { FormSearchableSelectField } from '../../../../presentation/components';
import { convergeLogger } from '@converge/config';

export interface InsuredEntity
  extends Omit<IUnderwritingQuoteExcessLayerParticipant, 'quote'> {}

export const InsurerSelectBox = ({
  name,
  insurerEntity,
  onChange,
  placeholder,
  exclude,
  isDisabled,
  onBlur,
  hideLabel = true,
  label,
  containerWidth,
  clearOnBlur = true,
}: {
  name: string;
  insurerEntity?: InsuredEntity | null;
  onChange: (broker: InsuredEntity | null) => void;
  exclude?: string[];
  placeholder?: string;
  isDisabled?: boolean;
  onBlur?: () => void;
  hideLabel?: boolean;
  label?: string;
  containerWidth?: number;
  clearOnBlur?: boolean;
}) => {
  const { entities = [], loading } = useGetEntities({
    isInsurer: true,
    pageSize: 200,
  });

  return (
    <FormSearchableSelectField
      minDropdownWidth="300px"
      maxDropdownWidth="300px"
      name={name}
      label={label}
      disableClearable={false}
      isLoading={loading}
      containerWidth={containerWidth}
      disabled={isDisabled}
      options={[...entities]
        .filter((entity) => {
          if (!exclude) {
            return true;
          }
          return !exclude?.includes(entity.uuid);
        })
        .sort((a, b) => {
          return (a.name || '').localeCompare(b.name || '');
        })}
      onBlur={onBlur ? () => onBlur() : undefined}
      mapToOption={(entity) => {
        return {
          value: entity.uuid,
          label: entity.name ?? 'Insurer',
          data: entity,
        };
      }}
      noOptionsText="No insurers available to add as participants to this layer"
      required
      hideLabel={hideLabel}
      value={insurerEntity === null ? null : insurerEntity?.uuid}
      placeholder={placeholder ?? insurerEntity?.name}
      resetAfterSelect
      clearOnBlur={clearOnBlur}
      onChangeOption={(value) => {
        if (!value) {
          onChange(null);
          return;
        }

        const entity = entities.find((entity) => {
          return entity.uuid === value.value;
        });

        if (entity) {
          onChange({
            ...entity.data.node,
            // NOTE: I think there might be a bug, where there might be some edge case where the entity was previously added and removed, so it might not technically be "new"
            isNew: true,
            name: getDisplayNameFromEntity(entity.data.node) ?? 'Insurer',
            uuid: entity.uuid ?? entity.name ?? `${Math.random() * 1000}`,
            coverages: [],
          });
        } else {
          convergeLogger.warn(
            `Broker agency ${value.value} was not found in the broker agency select box`
          );
        }
      }}
    />
  );
};

// 1. All the participants added prior to the fix from 20 minutes ago don't have subcoverages
// 2. Different problem: There is an MR that fixes this: If we have an existing participants and we pass in a
//    coverage, they primary's coverages and subcoverages overrides the participant's coverages and subcoverages.
//    Solution: don't pass in a participant.uuid if it's a new participant
